import React, { useEffect, useState, useRef } from 'react';
import taskAPI from '../api/taskAPI';
import workerAPI from '../api/workerAPI'; 
import { FaEdit, FaTrash } from 'react-icons/fa';
import UserDashboard from './UserDashboard';

const TeamTaskManager = () => {
  const [tasks, setTasks] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    assigneeId: '',
    today: new Date().toISOString().split('T')[0],
    deadline: '',
    status: 'Pending',
    description: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const tableRef = useRef(null);

  useEffect(() => {
    fetchTasks();
    fetchWorkers();
  }, []);

  const fetchWorkers = async () => {
  try {
    const res = await workerAPI.get('/');
    const allWorkers = res.data;
    const worker = JSON.parse(localStorage.getItem("worker"));

    let filteredWorkers = allWorkers;

    if (worker?.role === "TL") {
  // TL → show only developers in the same team
  filteredWorkers = allWorkers.filter(
    (w) => w.role === "Developer" && w.team === worker.team
  );
} else if (worker?.role === "Developer") {
  // Developer → only themselves
  filteredWorkers = allWorkers.filter(
    (w) => w._id === worker._id
  );
}
    setWorkers(filteredWorkers);
  } catch (err) {
    console.log(err);
  }
};

const fetchTasks = async () => {
  try {
    const res = await taskAPI.get('/');
    const allTasks = res.data;
    const worker = JSON.parse(localStorage.getItem("worker"));

    let filteredTasks = allTasks;

    if (worker?.role === "TL") {
      // TL → show only tasks of their team developers
      filteredTasks = allTasks.filter((task) => {
        const assignee = workers.find(w => w._id === task.assigneeId);
        return assignee?.role === "Developer" && assignee.team === worker.team;
      });
    } else if (worker?.role === "Developer") {
      // Developer → see only their own tasks
      filteredTasks = allTasks.filter((task) => task.assigneeId === worker._id);
    }
    // HR → sees all

    setTasks(filteredTasks);
  } catch (err) {
    console.log(err);
  }
};


// ✅ First load workers
  useEffect(() => {
    fetchWorkers();
  }, []);

  // ✅ Once workers ready, then load tasks
  useEffect(() => {
    if (workers.length > 0) {
      fetchTasks();
    }
  }, [workers]);


  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAddOrUpdateTask = async () => {
    if (!formData.title || !formData.assigneeId || !formData.deadline || !formData.description) {
      alert('Please fill all the required fields!');
      return;
    }

    try {
      if (isEditing) {
        await taskAPI.put(`/${editId}`, formData);
        alert('Task updated successfully!');
      } else {
        await taskAPI.post('', formData);
        alert('Task added successfully!');
      }
      fetchTasks();

      setFormData({
        title: '',
        assigneeId: '',
        today: new Date().toISOString().split('T')[0],
        deadline: '',
        status: 'Pending',
        description: ''
      });

      setIsEditing(false);
      setEditId(null);

      setTimeout(() => {
        if (tableRef.current) {
          tableRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);

    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteTask = async (id) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await taskAPI.delete(`/${id}`);
        fetchTasks();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const handleEditTask = (task) => {
    setFormData(task);
    setIsEditing(true);
    setEditId(task._id);
  };

  const getWorkerName = (id) => {
    const worker = workers.find(w => w._id === id);
    return worker ? worker.username : 'Unknown';
  };

  const filteredTasks = tasks.filter(task =>
    getWorkerName(task.assigneeId).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 p-6 pt-20">
      <UserDashboard />

      <h2 className="text-3xl font-bold font-mono text-purple-700 mb-3 text-center mt-3">
        Task Management
      </h2>

      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Form */}
        <div className="bg-white p-6 rounded-xl shadow-lg">
  <h3 className="text-xl font-semibold text-purple-600 mb-3">
    {isEditing ? 'Edit Task' : 'Assign New Task'}
  </h3>

  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="title">
    Task Title
  </label>
  <input
    id="title"
    type="text"
    name="title"
    value={formData.title}
    onChange={handleChange}
    placeholder="Task Title"
    className="border rounded-lg px-4 py-2 w-full mb-4"
  />

  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="assigneeId">
    Assign To
  </label>
  <select
    id="assigneeId"
    name="assigneeId"
    value={formData.assigneeId}
    onChange={handleChange}
    className="border rounded-lg px-4 py-2 w-full mb-3"
  >
    <option value="" disabled hidden>Select a worker</option>
    {workers.map(worker => (
      <option key={worker._id} value={worker._id}>
        {worker.username} 
      </option>
    ))}
  </select>

  <div className="grid grid-cols-2 gap-4 mb-3">
    <div>
      <label className="text-sm text-gray-700 font-medium mb-1 block" htmlFor="today">
        Today's Date
      </label>
      <input
        id="today"
        type="date"
        name="today"
        value={formData.today}
        readOnly
        className="border rounded-lg px-4 py-2 w-full bg-gray-100"
      />
    </div>
    <div>
      <label className="text-sm text-gray-700 font-medium mb-1 block" htmlFor="deadline">
        Deadline
      </label>
      <input
        id="deadline"
        type="date"
        name="deadline"
        value={formData.deadline}
        onChange={handleChange}
        className="border rounded-lg px-4 py-2 w-full"
      />
    </div>
  </div>

  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="status">
    Status
  </label>
  <select
    id="status"
    name="status"
    value={formData.status}
    onChange={handleChange}
    className="border rounded-lg px-4 py-2 w-full mb-3"
  >
    <option value="Pending">Pending</option>
    <option value="In Progress">In Progress</option>
    <option value="Completed">Completed</option>
  </select>

  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="description">
    Task Description
  </label>
  <textarea
    id="description"
    name="description"
    value={formData.description}
    onChange={handleChange}
    placeholder="Task Description"
    className="border rounded-lg px-4 py-2 w-full mb-3"
    rows="1"
  ></textarea>

  <button
    onClick={handleAddOrUpdateTask}
    className={`${
      isEditing ? 'bg-blue-500 hover:bg-blue-600' : 'bg-purple-500 hover:bg-purple-600'
    } text-white px-6 py-2 rounded-lg w-full`}
  >
    {isEditing ? 'Update Task' : 'Add Task'}
  </button>
</div>


        {/* Table */}
        <div ref={tableRef} className="bg-white p-4 rounded-xl shadow-lg overflow-x-auto">
          <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
  <h3 className="text-xl font-semibold text-purple-600">
    Assigned Tasks
  </h3>
  <input
    type="text"
    placeholder="Search by Assignee Name"
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    className="border px-4 py-2 rounded-lg w-full md:w-80"
  />
</div>

          <div className="overflow-y-auto max-h-[400px]">
          <table className="min-w-full table-auto text-sm">
            <thead className="bg-purple-100 text-purple-700 sticky top-0 z-10">
              <tr>
                <th className="py-3 px-4 text-left bg-purple-100">Today</th>
                <th className="py-3 px-4 text-left bg-purple-100">Assigned</th>
                <th className="py-3 px-4 text-left bg-purple-100">Title</th>
                <th className="py-3 px-4 text-left bg-purple-100">Description</th>
                <th className="py-3 px-4 text-left bg-purple-100">Status</th>
                <th className="py-3 px-4 text-left bg-purple-100">Actions</th>
              </tr>
            </thead>
              <tbody className="text-gray-700">
                {filteredTasks.map((task, idx) => (
                  <tr key={idx} className="border-t hover:bg-purple-50 transition">
                    <td className="py-2 px-4">{new Date(task.today).toLocaleDateString('en-GB')}</td>
                    <td className="py-2 px-4">{getWorkerName(task.assigneeId)}</td>
                    <td className="py-2 px-4">{task.title}</td>
                    <td className="py-2 px-4">{task.description}</td>
                    <td
                      className={`py-2 px-4 font-semibold ${
                        task.status === 'Completed'
                          ? 'text-green-600'
                          : task.status === 'In Progress'
                          ? 'text-blue-600'
                          : 'text-yellow-600'
                      }`}
                    >
                      {task.status}
                    </td>
                    <td className="py-4 px-4 flex gap-3 items-center">
                      <FaEdit
                        className="text-blue-500 cursor-pointer hover:text-blue-700"
                        onClick={() => handleEditTask(task)}
                      />
                      <FaTrash
                        className="text-red-500 cursor-pointer hover:text-red-700"
                        onClick={() => handleDeleteTask(task._id)}
                      />
                    </td>
                  </tr>
                ))}
                {filteredTasks.length === 0 && (
                  <tr>
                    <td colSpan="6" className="text-center py-6 text-gray-500">
                      No tasks assigned yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamTaskManager;
